<template>
  <div>
    <confirmation-modal
      :visible="displayCateringConfirmation"
      :intention="cateringConfirmationIntention"
      :mealType="mealTypeInUpperCase"
      :userOffice="userOffice"
      @toggleDisplay="toggleDisplayCateringConfirmation"
    />
    <el-autocomplete
      v-model="search"
      :fetch-suggestions="querySearch"
      :trigger-on-focus="false"
      :placeholder="placeholder"
      value-key="name"
      @select="handleSelect"
    >
      <i slot="suffix" class="el-icon-search el-input__icon"></i>
    </el-autocomplete>
    <el-alert
      v-if="hasError"
      :closable="false"
      title="User not found"
      type="error"
      show-icon
    ></el-alert>
  </div>
</template>
<script>
import { CATERING_CONFIRMATION_INTENTIONS } from '@/constants';
import { mapState } from 'vuex';
import get from 'lodash.get';
import Fuse from 'fuse.js';
import ConfirmationModal from '@/components/catering/ConfirmationModal';

const CATERING_CONFIRMATION = 'confirmation';

export default {
  props: {
    minlength: {
      type: Number,
      required: false,
      default: 0,
    },
    users: {
      type: Array,
      required: true,
    },
    settings: {
      type: Object,
      required: true,
    },
  },
  components: {
    ConfirmationModal,
  },
  data() {
    return {
      search: '',
      placeholder: this.$t('catering.searchPlaceholder'),
      hasError: false,
      order: null, // {success: boolean, message: string }
      cateringConfirmationIntention: 'success', // starts with success
      displayCateringConfirmation: false,
      mealTypeInUpperCase: null,
      selectedUser: null,
      userOffice: null,
      userMeal: null,
    };
  },
  computed: {
    ...mapState('catering', {
      mealsInfo: (state) => state.catering.data.mealsInfo,
    }),
    mealsServedHere() {
      const { settings } = this.settings;
      return settings.diets.map((d) => d.name);
    },
    ...mapState('offices', { offices: (state) => state.offices }),
  },
  methods: {
    querySearch(queryString, cb) {
      let results = [];
      let hasError = false;

      if (queryString.length >= this.minlength) {
        const options = {
          shouldSort: true,
          threshold: 0.3,
          location: 0,
          distance: 100,
          maxPatternLength: 32,
          minMatchCharLength: 3,
          keys: ['name', 'email'],
        };
        const fuse = new Fuse(this.users, options);
        results = fuse.search(queryString);
        hasError = results.length <= 0;
      }

      this.hasError = hasError;
      cb(results);
    },
    handleSelect(selectedUser) {
      this.validateUserOrder(selectedUser);
    },
    validateUserOrder(selectedUser) {
      const { settings } = this.settings;
      const { orders: userOrders, office: userOffice } = selectedUser;
      const { name: settingsEvent } = settings.event;
      const userMeal = userOrders ? userOrders[settingsEvent] : null;

      let response;

      // validate office
      if (userOffice !== settings.office.id) {
        response = { success: false, message: this.$t('catering.messages.incorrectOffice') };
        this.userOffice = this.offices.find(({ id }) => id === userOffice);
        this.cateringConfirmationIntention = CATERING_CONFIRMATION_INTENTIONS['wrongOffice'];
        this.displayCateringConfirmation = true;
      } else if (!userMeal) {
        // Validates Event
        response = { success: false, message: this.$t('catering.messages.incorrectMeal') };
        this.cateringConfirmationIntention = CATERING_CONFIRMATION_INTENTIONS['notRegisteredMeals'];
        this.displayCateringConfirmation = true;
      } else {
        // Validates meal/station
        response = this.validateMeal(settings, userMeal);
      }
      this.selectedUser = selectedUser;
      this.userMeal = userMeal;
      this.order = response;
    },
    validateMeal(settings, userMeal) {
      // check if this is the correct station
      const selectedMeal = this.mealsInfo.find((x) => x.id === userMeal.meal);
      let message = this.$t('catering.messages.orderError');
      let success = false;
      const { name: userOrder } = selectedMeal.dietInfo;
      this.mealTypeInUpperCase = userOrder.toUpperCase();

      // user has already taken his/her food, no matter the station
      if (userMeal.served) {
        this.cateringConfirmationIntention = CATERING_CONFIRMATION_INTENTIONS['mealAlreadyServed'];
        this.displayCateringConfirmation = true;
        message = this.$t('catering.messages.orderAlreadyServed');
      } else if (selectedMeal) {
        const isMealServedHere = this.mealsServedHere.includes(selectedMeal.dietInfo.name);

        if (isMealServedHere) {
          success = true;
          message = this.$t('catering.messages.orderSuccess');
          this.cateringConfirmationIntention = CATERING_CONFIRMATION_INTENTIONS['success'];
          this.displayCateringConfirmation = true;
        } else {
          message = this.$t('catering.messages.incorrectStation', { userOrder });

          this.cateringConfirmationIntention =
            CATERING_CONFIRMATION_INTENTIONS['incorrectMealType'];
          this.displayCateringConfirmation = true;
        }
      }

      return { message, success };
    },
    toggleDisplayCateringConfirmation(intention) {
      this.displayCateringConfirmation = !this.displayCateringConfirmation;

      if (intention === CATERING_CONFIRMATION_INTENTIONS.success) {
        const userId = get(this.selectedUser, 'id');
        const orderId = get(this.userMeal, 'id');
        this.$emit(CATERING_CONFIRMATION, { userId, orderId });
      }

      if (!this.displayCateringConfirmation) {
        this.search = '';
        this.userMeal = null;
        this.selectedUser = null;
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import '../../scss/typography';

$items-font-size: $base-font-size * 1.6; // 24px
$search-font-size: $base-font-size * 2; // 32px

/deep/.el-alert--error {
  height: $search-font-size;
  margin: 16px 0;

  .el-alert__icon {
    font-size: $items-font-size;
  }

  .el-alert__content {
    padding: 0 32px;

    .el-alert__title {
      font-size: $items-font-size;
      line-height: unset;
    }
  }
}

/deep/.el-autocomplete {
  .el-input {
    .el-input__inner {
      font-size: $search-font-size;
      height: 100%;
      padding: $base-font-size / 2;
      padding-right: $search-font-size + $base-font-size;
    }

    .el-input__icon {
      font-size: $search-font-size;
      padding: 0px $base-font-size / 2;
      width: unset;
    }
  }
  ::placeholder {
    line-height: normal;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/catering/UserAutocomplete.vue